import { useCallback, useLayoutEffect, useRef, useState } from 'react';
import useModalRestRelease from './useModalRestRelease';

/**
 * TAP-REST — touch text-field pop-ups OPEN CENTRED and glide up under the
 * header when the user TAPS a typing field (KAN-1181, founder decision
 * 2026-10-31; replaces the open-time rest latch of KAN-930 Option B, which
 * parked a card under the header the moment it opened).
 *
 * Every touch pop-up opens centred, like every other dialog (KAN-542). A user
 * tap on a typing field on a phone/tablet that can raise a software keyboard
 * (`preferMobile && keyboardAware && canPredictKeyboard()`) makes
 * `useModalKeyboardAnticipation` (+ `modalTapRest`) glide the card to the
 * header floor (`glideTopFloorPx()`) as the keyboard rises. From then on it is
 * a REST card for that typing session: `data-cvx-kb-rest`, body shrink
 * (`modalBodyShrink`), no freeze / curtain / spacer. When the keyboard closes
 * the same hook glides it back to the CENTRE in the same beat as the body
 * release, and the session ends (`onTapRest(false)`); a later tap repeats it.
 * The card is exactly its content height while typing, or shorter by exactly
 * what the keyboard covers — never taller, never a band.
 *
 * Unchanged: desktop, an iPad with a hardware keyboard (`canPredictKeyboard()`
 * false), a shell that is not `keyboardAware`, and `typingRest={false}` cards
 * (pre-sign-in cards) never rest; the last keep today's 1b lift path.
 * `typingRest={true}` means nothing extra any more (the tap decides) — the
 * prop is still accepted so callers keep working.
 *
 * This hook owns the session flag (`rest`) the shell reads for
 * `data-cvx-kb-rest` and the keyboard mode. The action beat still sends a
 * rested card back to the centre (`useModalRestRelease`). `fallback` /
 * `setFallback`: a rest card whose body shrink FAILED this typing session (set
 * by `useModalKeyboardAnticipation`) — the shell then gives the keyboard mode
 * today's curtain/glide styles back (KAN-930 fallback = today's path).
 *
 * @param {{ dialogEl: HTMLElement|null, mounted: boolean, preferMobile: boolean,
 *   typingRest?: boolean, keyboardAware?: boolean, keyboardOpen?: boolean,
 *   visible?: boolean }} args
 * @returns {{ rest: boolean, decided: boolean, tapRestEnabled: boolean,
 *   onTapRest: (on: boolean) => void, released: boolean, fallback: boolean,
 *   setFallback: Function, suppressBridge: { current: Function|null } }}
 */
export default function useModalTypingRest({
  dialogEl, mounted, preferMobile: preferMobileIn, typingRest, keyboardAware = true,
  keyboardOpen = false, visible = true,
}) {
  const [tapRested, setTapRested] = useState(false); // a tap-rested typing session is live
  const [fallback, setFallback] = useState(false);
  const [tapEpoch, setTapEpoch] = useState(0); // +1 per tap-rest engage: resets the beat release latch
  // Filled by useModalKeyboardAnticipation (it owns the shell's dismiss gate and
  // runs later in the shell): `suppressBridge.current(ms)` arms that gate.
  const suppressBridge = useRef(null);
  const tapRestEnabled = !!(preferMobileIn && keyboardAware && typingRest !== false);

  useLayoutEffect(() => {
    if (mounted) return;
    if (tapRested) setTapRested(false);
    if (fallback) setFallback(false);
  }, [mounted, tapRested, fallback]);

  const onTapRest = useCallback((on) => {
    setTapRested(on);
    if (on) setTapEpoch((n) => n + 1);
    if (!on) setFallback(false); // a new session starts clean
  }, []);

  // The action beat sends a rested card back to the centre (useModalRestRelease).
  const released = useModalRestRelease({
    dialogEl, mounted, rest: tapRested, keyboardOpen, visible, resetKey: tapEpoch,
    suppressDismiss: (ms) => { if (suppressBridge.current) suppressBridge.current(ms); },
  });
  const rest = tapRested && !released;
  return {
    rest,
    decided: true, // every touch pop-up opens centred: nothing to wait for
    tapRestEnabled,
    onTapRest,
    released,
    fallback: rest && fallback,
    setFallback,
    suppressBridge,
  };
}
